import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import './App.css';

const TOKEN_KEY = 'lda_auth_token';

function App() {
  const navigate = useNavigate();
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0); // 0:上传前, 1:分析中, 2:展示结果
  const [vizUrl, setVizUrl] = useState("");
  const [report, setReport] = useState("");
  const [loadingText, setLoadingText] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [keywordCount, setKeywordCount] = useState(4); // 默认4个关键词
  const [topicCount, setTopicCount] = useState(4); // 默认4个主题
  const [customStopwords, setCustomStopwords] = useState(""); // 自定义停用词表
  const [customDict, setCustomDict] = useState(""); // 自定义专用词词典

  const handleLogout = () => {
    if (window.confirm('确定要退出登录吗？')) {
      localStorage.removeItem(TOKEN_KEY);
      navigate('/login');
    }
  };

  const handleFileChange = (e) => {
    setFiles(e.target.files);
  };

  const startAnalysis = async () => {
    if (!files) return alert("请先选择文件！");
    if (!apiKey || !apiKey.trim()) return alert("请先填写Qwen API Key！");
    
    // 验证关键词数量
    const count = parseInt(keywordCount);
    if (isNaN(count) || count < 3 || count > 10) {
      return alert("关键词数量必须是3-10之间的整数！");
    }
    
    // 验证主题数量
    const topics = parseInt(topicCount);
    if (isNaN(topics) || topics < 3 || topics > 10) {
      return alert("主题数量必须是3-10之间的整数！");
    }
    
    setLoading(true);
    setStep(1);
    setLoadingText("正在进行 LDA 主题建模...");

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }
    
    // 添加主题数量
    formData.append("n_topics", topics.toString());
    
    // 添加自定义停用词和专用词词典
    if (customStopwords.trim()) {
      formData.append("custom_stopwords", customStopwords.trim());
    }
    if (customDict.trim()) {
      formData.append("custom_dict", customDict.trim());
    }

    // 根据环境自动选择API地址
    // 开发环境：直接访问 localhost:8000
    // 生产环境：通过nginx代理 /api
    const isDevelopment = window.location.hostname === 'localhost' && window.location.port === '3000';
    const apiBase = isDevelopment ? 'http://localhost:8000' : '/api';

    try {
      // 1. 请求 LDA 分析
      const ldaResponse = await axios.post(`${apiBase}/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setVizUrl(ldaResponse.data.visualization_url);
      const topicsData = ldaResponse.data.lda_data;

      // 2. 请求 Qwen 生成报告
      setLoadingText("LDA 建模完成，正在请求 Qwen 大模型撰写报告...");
      const reportResponse = await axios.post(`${apiBase}/generate_report`, {
        topics: topicsData,
        api_key: apiKey.trim(),
        keyword_count: count
      });

      setReport(reportResponse.data.report);
      setStep(2);
    } catch (error) {
      console.error(error);
      alert("分析失败，请检查后端是否启动 (port 8000)");
      setStep(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container">
      <header className="header">
        <div className="logo">
          <i className="fas fa-robot"></i>
          <span>湾小通</span>
        </div>
        <h1>LDA主题建模智能体</h1>
        <button 
          onClick={handleLogout} 
          style={{
            marginLeft: 'auto',
            padding: '8px 16px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
        >
          退出登录
        </button>
      </header>

      {step === 0 && (
        <div className="upload-section">
          <div className="card">
            <h2>开始新的分析</h2>
            <p>支持格式：TXT/CSV, Word (.docx),系统将自动进行 LDA 建模并生成 AI 报告。</p> {/* 修改提示文案 */}
            
            <input 
              type="text"
              placeholder="请输入Qwen API Key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="api-key-input"
            />
            
            <div style={{width: '100%', marginBottom: '10px'}}>
              <label style={{display: 'block', marginBottom: '5px', fontSize: '14px', color: '#555'}}>
                主题数量（3-10，默认4）:
              </label>
              <input 
                type="number"
                placeholder="请输入主题数量，默认4"
                value={topicCount}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (parseInt(val) >= 3 && parseInt(val) <= 10)) {
                    setTopicCount(val);
                  }
                }}
                min="3"
                max="10"
                className="api-key-input"
              />
            </div>
            
            <div style={{width: '100%', marginBottom: '10px'}}>
              <label style={{display: 'block', marginBottom: '5px', fontSize: '14px', color: '#555'}}>
                核心关键词数量（3-10，默认4）:
              </label>
              <input 
                type="number"
                placeholder="请输入数量，默认4"
                value={keywordCount}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || (parseInt(val) >= 3 && parseInt(val) <= 10)) {
                    setKeywordCount(val);
                  }
                }}
                min="3"
                max="10"
                className="api-key-input"
              />
            </div>
            
            <div style={{width: '100%', marginBottom: '10px', textAlign: 'left'}}>
              <label style={{display: 'block', marginBottom: '5px', fontSize: '14px', color: '#555'}}>
                自定义停用词表（可选，每行一个词）:
              </label>
              <textarea
                placeholder="请输入自定义停用词，每行一个词，例如：&#10;的&#10;了&#10;在"
                value={customStopwords}
                onChange={(e) => setCustomStopwords(e.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{width: '100%', marginBottom: '10px', textAlign: 'left'}}>
              <label style={{display: 'block', marginBottom: '5px', fontSize: '14px', color: '#555'}}>
                自定义专用词词典（可选，每行一个词）:
              </label>
              <textarea
                placeholder="请输入自定义专用词，每行一个词，例如：&#10;人工智能&#10;机器学习&#10;深度学习"
                value={customDict}
                onChange={(e) => setCustomDict(e.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <input 
              type="file"  multiple onChange={handleFileChange} className="file-input" accept=".txt,.docx,.doc" />
            <button onClick={startAnalysis} className="btn-primary" disabled={loading}>
              {loading ? "分析中..." : "开始全流程分析"}
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="loading-section">
          <div className="spinner"></div>
          <h3>{loadingText}</h3>
        </div>
      )}

      {step === 2 && (
        <div className="dashboard">
          {/* 左侧：LDA 可视化 */}
          <div className="pane left-pane">
            <div className="pane-header">LDA 主题模型可视化 (pyLDAvis)</div>
            <iframe 
              src={vizUrl} 
              title="LDA Visualization" 
              className="lda-iframe"
            />
          </div>

          {/* 右侧：AI 报告 */}
          <div className="pane right-pane">
            <div className="pane-header">🤖 Qwen 智能分析报告</div>
            <div className="markdown-content">
              <ReactMarkdown>{report}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;